import { formatOrigins, originsOfImei, originsOfImeis, type SgOrigins } from "./origins";
import type { SgLineaRow } from "./equipos";
import type { SgUserRow } from "./usuarios";
import type { XlsxCell } from "./xlsx";

// Filas de las exportaciones .xlsx de SG, puras para poder testearlas sin
// Supabase ni socket. IMEI, teléfono y SIM salen SIEMPRE como string: en el
// .xlsx eso es celda de texto y Excel ya no los redondea (un IMEI de 15 dígitos
// o un ICCID de 19-20 no caben en un double). Por eso desapareció el «'» que
// se les pegaba en el CSV y la columna duplicada «imei texto» del barrido.

export const mapsUrl = (lat: number | null, lon: number | null) =>
  lat != null && lon != null ? `https://maps.google.com/?q=${lat},${lon}` : "";

const str = (v: unknown) => (v == null ? "" : String(v));
const date = (ts: unknown): Date | null => {
  if (ts == null || ts === "") return null;
  const d = new Date(ts as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
};
const coords = (p: any): XlsxCell[] => {
  const lat = p?.point?.lat ?? null;
  const lon = p?.point?.lon ?? null;
  return [lat, lon, mapsUrl(lat, lon)];
};

export const BARRIDO_HEADERS = [
  "imei",
  "linea telefonica",
  "simcard",
  "usuario",
  "estado de usuario",
  "alias",
  "marca",
  "modelo",
  "estado de equipo",
  "etiqueta de equipo",
  "ultimo reporte",
  "proveedor",
  "plataformas",
  "latitud",
  "longitud",
  "mapa",
];

// d: equipo de SG con joins; p: última posición del socket de sg-realtime.
export function barridoRow(d: any, p: any, origins?: SgOrigins): XlsxCell[] {
  const u = d.users || {};
  return [
    str(d.imei),
    str(d.phone),
    str(d.simcard),
    str(u.user_name),
    str(u.user_statuses?.name),
    str(d.name),
    str(d.brands?.brand),
    str(d.brands?.model),
    str(d.statuses?.name),
    str(d.device_conditions?.name),
    date(p?.last_comm_ts ?? d.last_time_connected),
    str(p?.provider),
    formatOrigins(originsOfImei(origins, d.imei)),
    ...coords(p),
  ];
}

export const LINEAS_HEADERS = [
  "imei",
  "linea telefonica",
  "simcard",
  "usuario",
  "estado de usuario",
  "alias",
  "modelo",
  "estado de equipo",
];

export const lineaRow = (r: SgLineaRow): XlsxCell[] => [
  str(r.imei),
  str(r.phone),
  str(r.simcard),
  str(r.user_name),
  str(r.user_status),
  str(r.alias),
  str(r.model),
  str(r.device_status),
];

export function deviceRow(d: any, p: any, origins?: SgOrigins): XlsxCell[] {
  return [
    str(d.name),
    str(d.uniq_id),
    str(d.users?.user_name),
    str(d.brands?.brand),
    str(d.brands?.model),
    date(p?.last_comm_ts ?? d.last_time_connected),
    str(d.phone),
    str(d.statuses?.name_es || d.statuses?.name),
    str(d.device_conditions?.name),
    str(p?.provider),
    formatOrigins(originsOfImei(origins, d.imei)),
    ...coords(p),
  ];
}

export const userRow = (
  u: SgUserRow,
  imeis: string[],
  origins?: SgOrigins
): XlsxCell[] => [
  str(u.user_name),
  formatOrigins(originsOfImeis(origins, imeis)),
  u.inactivos,
  u.activos,
  u.facturables,
  u.total,
  str(u.business_name),
  str(u.status_name),
];

// Unidades de un usuario (ficha). lastConn: timestamp en vivo o el de SG.
export const userDeviceRow = (
  d: any,
  lastConn: unknown,
  origins?: SgOrigins
): XlsxCell[] => [
  str(d.name),
  str(d.uniq_id),
  str(d.imei),
  str(d.statuses?.name_es || d.statuses?.name),
  str(d.brands?.model),
  date(lastConn),
  str(d.phone),
  formatOrigins(originsOfImei(origins, d.imei)),
];

// Fecha LOCAL (YYYY-MM-DD) para el nombre del archivo. Con toISOString era la
// fecha UTC: de las 18:00 en adelante (UTC-6) el archivo salía con la de mañana.
export const fileDate = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
